import { listUtils } from '@festina-life/core'
import type { FC, ReactNode } from 'react'

import { isApiError } from '../../../components/errorFallback/utils'
import { useHook } from '../../../lenteHooks'
import { useAvailableComponents } from '../../useAvailableComponents'
import type { InternalFnToSearchParams } from './types'
import { useInternalIdResolver } from './useInternalIdResolver'

export const httpCodesLackOfAccess = [401, 403]

type Entity = 'person' | 'company' | 'invoice'
type Navigation = () => void | Promise<void>

export type SearchParams = {
  view: FC<{ navigation: Navigation }>
  components: {
    // TODO `components` is a terrible name
    navigation: Navigation
    fetch: () => Promise<boolean>
    isAvailable: boolean
  }[]
}

export type SearchResult = {
  entity: Entity
  internalId: string
} & (
  | {
      success: true
      view: ReactNode
      navigation: Navigation
    }
  | {
      success: false
      reason: 'noAccess' | 'other' | 'notFound'
    }
)

type Props = {
  internalOrExternalId: string
  entity: Record<`${Entity}Search`, InternalFnToSearchParams>
}

const createSearchPromises = ({
  entity,
  search,
  internalId,
}: {
  entity: Entity
  search: InternalFnToSearchParams
  internalId: string
}): Promise<SearchResult>[] => {
  const { view: View, components } = search(internalId)
  return components
    .filter(({ isAvailable }) => isAvailable)
    .map((component) =>
      (async (component): Promise<SearchResult> => {
        try {
          const hasData = await component.fetch()
          if (hasData) {
            return {
              entity,
              internalId,
              success: true,
              view: <View navigation={component.navigation} />,
              navigation: component.navigation,
            }
          } else {
            return {
              entity,
              internalId,
              success: false,
              reason: 'notFound',
            }
          }
        } catch (e) {
          return {
            entity,
            internalId,
            success: false,
            reason:
              isApiError(e) && httpCodesLackOfAccess.includes(e.status)
                ? 'noAccess'
                : 'other',
          }
        }
      })(component),
    )
}

const isSuccessResult = (
  result: SearchResult,
): result is SearchResult & { success: true } => result.success

/**
 * Is a helper hook that encapsulates the logic for searching across multiple entity types
 * (such as people, companies, and invoices) using a provided identifier. It coordinates the search requests,
 * collects their results, and determines if any access errors occurred. It returns a function that, when called, performs these searches and returns a summary of the results.
 */
export const useSearchNavigateTemplate = () => {
  const { isUsingLegalEntity } = useAvailableComponents()
  const { isUsingPersonEntity, isUsingCompanyEntity, isUsingInvoiceEntity } =
    useHook('general.search.useAvailableEntities')()
  const findInternalIds = useInternalIdResolver()

  return async (props: Props) => {
    const { entity, internalOrExternalId } = props

    const idList = isUsingLegalEntity
      ? await findInternalIds(internalOrExternalId)
      : [internalOrExternalId]

    const allResults = await Promise.allSettled([
      ...(isUsingPersonEntity ? idList : []).flatMap((internalId) =>
        createSearchPromises({
          entity: 'person',
          internalId,
          search: entity.personSearch,
        }),
      ),
      ...(isUsingCompanyEntity ? idList : []).flatMap((internalId) =>
        createSearchPromises({
          entity: 'company',
          internalId,
          search: entity.companySearch,
        }),
      ),
      ...(isUsingInvoiceEntity ? [internalOrExternalId] : []).flatMap(
        (internalId) =>
          createSearchPromises({
            entity: 'invoice',
            internalId,
            search: entity.invoiceSearch,
          }),
      ),
    ])

    const fulfilled = [
      ...listUtils
        .groupBy(
          allResults.flatMap((result) =>
            result.status === 'fulfilled' ? [result.value] : [],
          ),
          (result) => `${result.entity}:${result.internalId}`,
        )
        .values(),
    ]

    return {
      successfulRequests: fulfilled.flatMap((result) => {
        const success = result.find(isSuccessResult)
        return success ? [success] : []
      }),
      didSomeRequestsFailDueToAccess:
        !!fulfilled.length &&
        fulfilled.flat().some((x) => !x.success && x.reason === 'noAccess'),
      didAllRequestsFailDueToLackOfAccess:
        !!fulfilled.length &&
        fulfilled.flat().every((x) => !x.success && x.reason === 'noAccess'),
    }
  }
}
